import { Brand, Domain } from "../types/domains";
import { brandsArray } from "./brandsArray";
import { combinedConfig } from "./combinedConfig";
import { logger } from "../utils/logging";

/**
 * Source de la liste des marques.
 *
 * Au boot (`initBrands`, appelé par index.tsx AVANT l'import des modules qui
 * évaluent la config), le drawer tente de charger brands.json depuis le CDN —
 * la liste y est éditable via l'API admin du dashboard, donc un domaine ou un
 * client ajouté est pris en compte sans redéploiement. La config UI n'y est
 * pas inlinée : chaque entrée porte un `configKey` résolu localement dans
 * `combinedConfig`. En cas d'échec (réseau, timeout, JSON invalide), fallback
 * transparent sur `brandsArray` local, c'est-à-dire le comportement historique.
 */

const BRANDS_CDN_URL = "https://cdn.kleep.ai/brands.json";
const FETCH_TIMEOUT_MS = 2500;

type CdnDomain = {
  name: string;
  unitSystem?: Domain["unitSystem"];
  defaultBraSize?: Domain["defaultBraSize"];
};

export type CdnBrand = {
  name: string;
  uuid: string | null;
  domains: CdnDomain[];
  configKey: string | null;
};

let activeBrands: Brand[] = brandsArray;
let activeSource: "local" | "cdn" = "local";

// Même fallback que le getConfig historique : The Kooples, sinon la première.
const defaultConfig =
  brandsArray.find((b) => b.name === "The Kooples")?.config ??
  brandsArray[0]?.config;

const isValidDomain = (d: CdnDomain): boolean =>
  typeof d?.name === "string" && d.name !== "";

const isValidBrand = (b: CdnBrand): boolean =>
  typeof b?.name === "string" &&
  b.name !== "" &&
  Array.isArray(b?.domains) &&
  b.domains.length > 0 &&
  b.domains.every(isValidDomain);

/**
 * Transforme la liste CDN en Brand[] interne. Les entrées malformées sont
 * écartées (liste partiellement corrompue ≠ tout perdre) ; un configKey
 * inconnu ou null retombe sur la config par défaut.
 */
export const resolveCdnBrands = (raw: CdnBrand[]): Brand[] => {
  const configByKey = combinedConfig as Record<
    string,
    { config: Brand["config"] }
  >;
  const dropped: string[] = [];
  const brands: Brand[] = [];

  for (const entry of raw) {
    if (!isValidBrand(entry)) {
      dropped.push(typeof entry?.name === "string" ? entry.name : "<sans nom>");
      continue;
    }
    brands.push({
      name: entry.name,
      uuid: entry.uuid ?? null,
      domains: entry.domains.map((d) => ({
        name: d.name,
        unitSystem: d.unitSystem === "imperial" ? "imperial" : "metric",
        ...(d.defaultBraSize ? { defaultBraSize: d.defaultBraSize } : {}),
      })),
      config:
        (entry.configKey && configByKey[entry.configKey]?.config) ||
        defaultConfig,
    });
  }

  if (dropped.length) {
    logger.warn(`brands.json: entrées CDN écartées: ${dropped.join(", ")}`);
  }
  return brands;
};

/**
 * Charge la liste des marques depuis le CDN. Ne rejette jamais : en cas
 * d'échec le fallback local reste actif et l'app boote normalement.
 */
export const initBrands = async (): Promise<void> => {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    const response = await fetch(BRANDS_CDN_URL, { signal: controller.signal });
    clearTimeout(timer);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const raw: CdnBrand[] = await response.json();
    if (!Array.isArray(raw)) {
      throw new Error("brands.json n'est pas un tableau");
    }
    const brands = resolveCdnBrands(raw);
    if (brands.length === 0) {
      throw new Error("liste CDN vide après validation");
    }
    activeBrands = brands;
    activeSource = "cdn";
    logger.log(`✅ Brands: CDN (${brands.length} marques)`);
  } catch (error) {
    logger.warn(
      `Brands: échec CDN (${
        error instanceof Error ? error.message : String(error)
      }), fallback local (${brandsArray.length} marques)`,
    );
  }
};

/** Liste active (CDN si chargée, sinon brandsArray local). */
export const getBrands = (): Brand[] => activeBrands;

/** Retailer public uuid d'une marque (CDN uniquement ; null sinon). */
export const getBrandUuid = (brandName: string): string | null =>
  activeBrands.find((b) => b.name === brandName)?.uuid ?? null;

/** D'où vient la liste active — exposé pour les logs/tests. */
export const getBrandsSource = (): "local" | "cdn" => activeSource;
